/**
 * Modal szczegolow faktury z KSeF (GET /ksef/otrzymane/<id>; w trybie 'koszty' - GET /external-costs/z-ksef/<id> przez
 * `zrodlo` {szczegoly(id), pobierzXml(id)}, bez akcji magazynowych i bez kolumny "Produkt w systemie"):
 * sekcje "Płatność" (forma, termin z opisem, zaplacono / po terminie, zaplata czesciowa, skonto), "Rachunki bankowe"
 * (numer w grupach, bank, SWIFT, faktor), "Warunki transakcji" (zamowienia, umowy, transport, adnotacje, dodatkowy opis,
 * rozliczenie, kontakt sprzedawcy, rejestry i stopka) - z `platnosc` i `szczegoly_xml` (utils/ksef_fa_parser.py).
 * Naglowek (numer, pelny numer KSeF, rodzaj, status,
 * sprzedawca z NIP i adresem z XML, NIP nabywcy, daty i platnosc z faktura_xml, sklep z dopasowania, kwoty),
 * dla korekt dane faktury korygowanej (faktura_xml.korekta, zapasowo dopasowanie.korygowana), tabela pozycji
 * z podgladem dopasowania produktu z backendu (zielony wiersz = pozycje[].produkt, pomaranczowy = brak),
 * lista `brakujace` (kod SFD, nazwa, ilosc, EAN), `pokrycie`; w stopce "Dopasuj ponownie", "XML" i "Dodaj jako
 * fakturę zakupu" (AkcjeDodaj - ten sam flow co w tabeli; widoczny wg mozna_dodac wiersza tabeli, przygaszony gdy braki).
 *
 * Naglowek bierze z odpowiedzi szczegolow, a gdy backend ich nie dolozy - z wiersza tabeli (`wiersz`).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ksefOtrzymaneService } from '../../services/ksefOtrzymaneService';
import {
  formatujKwote, formatujDate, formatujDateCzas, formatujIlosc, komunikatBledu, RODZAJE, DOPASOWANIA, jestKorekta, sklepWiersza,
  fakturaWSystemie,
} from './format';
import { ZnacznikStatusu, ZnacznikRodzaju, ZnacznikSugestii, AkcjeDodaj, AkcjeXml, pobierzXmlDoPliku } from './TabelaKsef';

const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const th = { padding: '4px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'top' };
const prawa = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const etykieta = { color: 'var(--text-muted)', fontSize: '11px' };

const sekcjaTytul = { fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' };
const sekcjaBox = { padding: '8px 10px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg)', marginBottom: '10px' };
const STANY_PLATNOSCI = {
  zaplacona: { etykieta: 'zapłacona', kolor: 'var(--success)', tlo: 'var(--success-light)' },
  czesciowa: { etykieta: 'zapłacona częściowo', kolor: 'var(--info)', tlo: 'var(--info-light)' },
  po_terminie: { etykieta: 'po terminie', kolor: 'var(--danger)', tlo: 'var(--danger-light)' },
  do_zaplaty: { etykieta: 'do zapłaty', kolor: 'var(--warning)', tlo: 'var(--warning-light)' },
  nieznany: { etykieta: 'bez terminu', kolor: 'var(--text-muted)', tlo: 'var(--bg)' },
};

/** Pozycje z ksef_fa_parser (szczegoly_xml.pozycje) -> ksztalt pozycji parse_fa3 (zapas, gdy backend nie dal `pozycje`). */
const pozycjeZXml = (lista) => (Array.isArray(lista) ? lista : []).map(p => ({
  lp: p.lp, nazwa_produktu: p.nazwa, kod_produktu: p.indeks || '', ean: p.gtin || '', jednostka: p.jm, ilosc: p.ilosc,
  cena_netto: p.cena_netto, stawka_vat: p.stawka_vat_proc ?? p.stawka_vat, kwota_vat: p.kwota_vat, wartosc_netto: p.wartosc_netto,
  wartosc_brutto: p.wartosc_brutto, produkt: null, dopasowanie: null,
}));

const SzczegolyKsefModal = ({ id, wiersz, sklepy, onZamknij, onZmiana, onDodano, tryb = 'admin', zrodlo }) => {
  const [dane, setDane] = useState(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);
  const [dopasowywanie, setDopasowywanie] = useState(false);
  const koszty = tryb === 'koszty';

  const laduj = useCallback(async () => {
    setLadowanie(true);
    try {
      const d = await (zrodlo?.szczegoly || ksefOtrzymaneService.szczegoly)(id);
      setDane(d || {});
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać szczegółów faktury'));
    } finally {
      setLadowanie(false);
    }
  }, [id, zrodlo]);

  useEffect(() => { laduj(); }, [laduj]);

  // Naglowek: szczegoly maja pierwszenstwo, wiersz z tabeli jako zapas
  const f = { ...(wiersz || {}), ...(dane || {}) };
  const fx = dane?.faktura_xml || {};
  const sx = dane?.szczegoly_xml || null;
  const pl = dane?.platnosc || null;
  const stanPl = pl ? (STANY_PLATNOSCI[pl.stan] || STANY_PLATNOSCI.nieznany) : null;
  const pozycje = Array.isArray(dane?.pozycje) && dane.pozycje.length > 0 ? dane.pozycje : pozycjeZXml(sx?.pozycje);
  const brakujace = Array.isArray(dane?.brakujace) ? dane.brakujace : [];
  const korekta = jestKorekta(f);
  const korygowana = fx.korekta || f.dopasowanie?.korygowana || null;
  const pokrycie = dane?.pokrycie || { dopasowane: pozycje.filter(p => p.produkt).length, wszystkie: pozycje.length };
  const sumaNetto = pozycje.reduce((a, p) => a + (Number(p.wartosc_netto) || 0), 0);
  const sumaBrutto = pozycje.reduce((a, p) => a + (Number(p.wartosc_brutto) || 0), 0);
  const listaSklepow = Array.isArray(dane?.sklepy) && dane.sklepy.length > 0 ? dane.sklepy : (Array.isArray(sklepy) ? sklepy : []);
  const sklep = sklepWiersza(f);
  const nazwaSklepu = sklep ? (sklep.nazwa || listaSklepow.find(s => Number(s.id) === Number(sklep.id))?.nazwa || `Sklep #${sklep.id}`) : null;
  const wSystemie = dane?.faktura_w_systemie || fakturaWSystemie(f);
  // "Dodaj" widoczny wg reguly statusu (wiersz tabeli); szczegoly zaostrzaja mozna_dodac o pokrycie pozycji -> braki = przygaszony
  const dodajWidoczny = Boolean(wiersz ? wiersz.mozna_dodac : dane?.mozna_dodac);

  const dopasuj = async () => {
    setDopasowywanie(true);
    try {
      const w = await ksefOtrzymaneService.dopasuj(id);
      toast.success(`Dopasowano ponownie fakturę ${f.numer_faktury || `#${id}`}`);
      await laduj();
      if (onZmiana) onZmiana(w, wiersz);
    } catch (err) {
      if (!err?.isAuthError) toast.error(komunikatBledu(err, 'Nie udało się dopasować faktury.'));
    } finally {
      setDopasowywanie(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="modal-ksef-tytul" data-testid="modal-szczegoly-ksef"
        style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '1000px', padding: '1.25rem', fontSize: '12px', color: 'var(--text)', maxHeight: '90vh', overflow: 'auto' }}>

        {/* Naglowek */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div>
            <h5 id="modal-ksef-tytul" style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700 }}>
              {RODZAJE[f.rodzaj]?.pelna || 'Faktura'} {f.numer_faktury || `#${id}`}
            </h5>
            <div data-testid="ksef-naglowek" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <ZnacznikStatusu wiersz={f} />
              <ZnacznikRodzaju rodzaj={f.rodzaj} />
              {f.sugestia && <span><span style={etykieta}>sugestia </span><ZnacznikSugestii sugestia={f.sugestia} /></span>}
              <span><span style={etykieta}>wystawiona </span>{formatujDate(f.data_wystawienia)}</span>
              {f.data_nadania_ksef && <span><span style={etykieta}>nadana do KSeF </span>{formatujDateCzas(f.data_nadania_ksef)}</span>}
              {fx.data_dostawy && <span><span style={etykieta}>dostawa </span>{formatujDate(fx.data_dostawy)}</span>}
              {fx.data_platnosci && <span><span style={etykieta}>termin płatności </span>{formatujDate(fx.data_platnosci)}</span>}
              {fx.sposob_platnosci && <span><span style={etykieta}>płatność </span>{fx.sposob_platnosci}</span>}
              {f.tryb && <span><span style={etykieta}>tryb </span>{f.tryb}</span>}
            </div>
            <div data-testid="ksef-numer-pelny" style={{ marginTop: '4px', fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
              KSeF: {f.ksef_numer || '—'}
            </div>
            <div data-testid="ksef-strony" style={{ marginTop: '4px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span><span style={etykieta}>sprzedawca </span><strong>{f.sprzedawca_nazwa || fx.dostawca_nazwa || '—'}</strong>{f.sprzedawca_nip && <> · NIP {f.sprzedawca_nip}</>}{(fx.dostawca_adres || sx?.sprzedawca?.adres_tekst) && <> · {fx.dostawca_adres || sx.sprzedawca.adres_tekst}</>}
                {sx?.sprzedawca?.email && <span data-testid="sprzedawca-email"> · {sx.sprzedawca.email}</span>}
                {sx?.sprzedawca?.telefon && <span data-testid="sprzedawca-telefon"> · tel. {sx.sprzedawca.telefon}</span>}
              </span>
              {f.nabywca_nip && <span><span style={etykieta}>nabywca NIP </span>{f.nabywca_nip}</span>}
              <span><span style={etykieta}>sklep </span><strong>{nazwaSklepu || 'nieustalony'}</strong>{sklep && <span style={etykieta}> ({sklep.zrodlo === 'faktura' ? 'wg faktury w systemie' : 'wg adresu dostawy SFD'})</span>}</span>
            </div>
            <div data-testid="ksef-kwoty" style={{ marginTop: '4px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span><span style={etykieta}>netto </span>{formatujKwote(f.netto)}</span>
              <span><span style={etykieta}>VAT </span>{formatujKwote(f.vat)}</span>
              <span><span style={etykieta}>brutto </span><strong style={{ color: 'var(--orange)' }}>{formatujKwote(f.brutto)}</strong>{f.waluta && f.waluta !== 'PLN' ? ` ${f.waluta}` : ''}</span>
            </div>
            {f.status === 'pominieta' && f.powod_pominiecia && (
              <div data-testid="ksef-powod" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>Pominięta: {f.powod_pominiecia}</div>
            )}
            {f.xml_blad && <div data-testid="ksef-xml-blad" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--danger)' }}>Błąd XML: {f.xml_blad}</div>}
            {dane?.xml_parse_blad && <div data-testid="ksef-xml-parse-blad" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--danger)' }}>Nie udało się odczytać XML FA(3): {dane.xml_parse_blad}</div>}
            {wSystemie && (wSystemie.id || wSystemie.numer) && (
              <div data-testid="ksef-w-systemie" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--info)' }}>
                W systemie: {wSystemie.id && !koszty
                  ? <Link to={`/purchase-invoices/${wSystemie.id}`} style={{ color: 'var(--info)', fontWeight: 600 }}>{wSystemie.numer || `#${wSystemie.id}`}</Link>
                  : <strong>{wSystemie.numer || `#${wSystemie.id}`}</strong>}
                {wSystemie.sklep && <> · {wSystemie.sklep}</>}
                {wSystemie.kto && <> · dodał: {wSystemie.kto}</>}
                {f.dopasowanie?.kwota_zgodna === false && <span style={{ color: 'var(--warning)', fontWeight: 600 }}> · inna kwota niż w KSeF</span>}
              </div>
            )}
            {f.dopasowanie?.kontrola && (
              <div data-testid="ksef-kontrola" style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                Kontrola faktur SFD: wiersz #{f.dopasowanie.kontrola.id}{f.dopasowanie.kontrola.plik_format === 'fa3' ? ' — XML z KSeF podpięty jako plik dokumentu' : ''}
              </div>
            )}
          </div>
          <button type="button" onClick={onZamknij} aria-label="Zamknij" style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>✕</button>
        </div>

        {blad && <div data-testid="modal-blad" style={{ padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', marginBottom: '10px' }}>{blad}</div>}

        {/* Faktura korygowana */}
        {korekta && (
          <div data-testid="faktura-korygowana-box" style={{ padding: '8px 10px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--orange-light)', marginBottom: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '11px', color: 'var(--orange)', marginBottom: '4px' }}>Faktura korygowana</div>
            {korygowana ? (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong>{korygowana.numer_korygowanej || f.numer_korygowanej || '—'}</strong>
                {korygowana.data_korygowanej && <span><span style={etykieta}>data </span>{formatujDate(korygowana.data_korygowanej)}</span>}
                {(korygowana.ksef_numer_korygowanej || f.ksef_numer_korygowanej) && <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)' }}>KSeF {korygowana.ksef_numer_korygowanej || f.ksef_numer_korygowanej}</span>}
                {korygowana.przyczyna && <span><span style={etykieta}>przyczyna </span>{korygowana.przyczyna}</span>}
                {korygowana.forma && <span><span style={etykieta}>forma </span>{korygowana.forma === 'stan_przed_po' ? 'stan przed / po' : 'różnicowa'}</span>}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>{ladowanie ? 'Ładowanie…' : 'Brak danych faktury korygowanej w XML'}</div>
            )}
            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>Korekty z KSeF są tylko podglądem — dodawanie korekt SFD przez „Kontrolę korekt SFD”.</div>
          </div>
        )}

        {/* Platnosc (migracja 057 / ksef_fa_parser) */}
        {pl && (
          <div data-testid="ksef-platnosc" data-stan={pl.stan} style={sekcjaBox}>
            <div style={sekcjaTytul}>Płatność</div>
            {!pl.ma_platnosc && !pl.forma_platnosci && !pl.termin_platnosci && !pl.zaplacono ? (
              <div style={{ color: 'var(--text-muted)' }}>{f.ma_xml ? 'XML faktury nie ma sekcji Płatność (sprzedawca nie podał formy ani terminu).' : 'Brak XML — dane o płatności pojawią się po pobraniu pliku z KSeF.'}</div>
            ) : (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span><span style={etykieta}>forma </span><strong data-testid="platnosc-forma">{pl.forma_platnosci || 'nieznana'}</strong>{pl.forma_platnosci_kod ? <span style={etykieta}> (kod {pl.forma_platnosci_kod})</span> : null}</span>
                {pl.opis_platnosci && !String(pl.forma_platnosci || '').includes(pl.opis_platnosci) && <span><span style={etykieta}>opis </span>{pl.opis_platnosci}</span>}
                <span data-testid="platnosc-stan" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontWeight: 600, color: stanPl.kolor, backgroundColor: stanPl.tlo }}>
                  {stanPl.etykieta}{pl.zaplacono && pl.data_zaplaty ? ` ${formatujDate(pl.data_zaplaty)}` : ''}
                </span>
                {(pl.terminy?.length ? pl.terminy : (pl.termin_platnosci ? [{ termin: pl.termin_platnosci }] : [])).map((t, i) => (
                  <span key={i} data-testid="platnosc-termin"><span style={etykieta}>termin </span><strong>{t.termin ? formatujDate(t.termin) : '—'}</strong>
                    {t.opis && ((t.opis.ilosc !== null && t.opis.ilosc !== undefined) || t.opis.zdarzenie) && (
                      <span style={etykieta}> ({[t.opis.ilosc !== null && t.opis.ilosc !== undefined ? `${t.opis.ilosc} ${t.opis.jednostka || ''}`.trim() : null, t.opis.zdarzenie].filter(Boolean).join(' ')})</span>
                    )}
                  </span>
                ))}
                {pl.zaplata_czesciowa?.length > 0 && (
                  <span data-testid="platnosc-czesciowa"><span style={etykieta}>zapłacono częściowo </span>
                    {pl.zaplata_czesciowa.map((z, i) => `${formatujKwote(z.kwota)}${z.data ? ` (${formatujDate(z.data)})` : ''}`).join(', ')}
                  </span>
                )}
                {pl.skonto && <span data-testid="platnosc-skonto"><span style={etykieta}>skonto </span>{pl.skonto.warunki || ''}{pl.skonto.wysokosc ? ` — ${pl.skonto.wysokosc}` : ''}</span>}
              </div>
            )}
          </div>
        )}

        {/* Rachunki bankowe */}
        {pl && pl.rachunki?.length > 0 && (
          <div data-testid="ksef-rachunki" style={sekcjaBox}>
            <div style={sekcjaTytul}>Rachunki bankowe sprzedawcy</div>
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {pl.rachunki.map((r, i) => (
                  <tr key={`${r.nr}-${i}`} data-testid="rachunek-wiersz">
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: 'none', paddingLeft: 0 }}>{r.nr_formatowany || r.nr}</td>
                    <td style={{ ...td, borderBottom: 'none' }}>{r.bank || <span style={etykieta}>bank nieznany</span>}</td>
                    <td style={{ ...td, borderBottom: 'none', color: 'var(--text-muted)' }}>{[r.swift ? `SWIFT ${r.swift}` : null, r.opis, r.wlasny_banku ? 'rachunek własny banku' : null].filter(Boolean).join(' · ')}</td>
                    <td style={{ ...td, borderBottom: 'none' }}>{r.faktora && <span data-testid="rachunek-faktora" style={{ padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: 600, background: 'var(--orange-light)', color: 'var(--orange)' }}>rachunek faktora</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Numer z XML faktury (Fa/Platnosc/RachunekBankowy) — przed przelewem porównaj z fakturą / umową.</div>
          </div>
        )}

        {/* Warunki transakcji + reszta z XML */}
        {sx && (() => {
          const w = sx.warunki_transakcji || {};
          const zam = (w.zamowienia || []).filter(z => z.nr || z.data);
          const um = (w.umowy || []).filter(u => u.nr || u.data);
          const transport = w.transport || [];
          const adn = sx.adnotacje?.lista || [];
          const opisy = (sx.dodatkowy_opis || []).filter(o => o.klucz || o.wartosc);
          const roz = sx.rozliczenie;
          const obc = roz ? [...(roz.obciazenia || []).map(o => ({ ...o, typ: 'obciążenie' })), ...(roz.odliczenia || []).map(o => ({ ...o, typ: 'odliczenie' }))] : [];
          const rej = (sx.stopka?.rejestry || []).flatMap(r => [r.pelna_nazwa ? `pełna nazwa: ${r.pelna_nazwa}` : null, r.krs ? `KRS ${r.krs}` : null, r.regon ? `REGON ${r.regon}` : null, r.bdo ? `BDO ${r.bdo}` : null].filter(Boolean));
          const info = sx.stopka?.informacje || [];
          const p3 = sx.podmioty3 || [];
          const fa = sx.faktura || {};
          const jest = zam.length || um.length || transport.length || adn.length || opisy.length || obc.length || rej.length || info.length || p3.length
            || (w.nr_partii || []).length || w.warunki_dostawy || fa.okres_od || fa.wz?.length || sx.korekta;
          if (!jest) return null;
          return (
            <div data-testid="ksef-warunki" style={sekcjaBox}>
              <div style={sekcjaTytul}>Warunki transakcji i pozostałe dane z XML</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '3px 10px', alignItems: 'baseline' }}>
                {(fa.okres_od || fa.okres_do) && <><span style={etykieta}>okres</span><span>{formatujDate(fa.okres_od)} – {formatujDate(fa.okres_do)}</span></>}
                {fa.wz?.length > 0 && <><span style={etykieta}>WZ</span><span>{fa.wz.join(', ')}</span></>}
                {zam.length > 0 && <><span style={etykieta}>zamówienia</span><span data-testid="warunki-zamowienia">{zam.map(z => `${z.nr || '—'}${z.data ? ` (${formatujDate(z.data)})` : ''}`).join(', ')}</span></>}
                {um.length > 0 && <><span style={etykieta}>umowy</span><span data-testid="warunki-umowy">{um.map(u => `${u.nr || '—'}${u.data ? ` (${formatujDate(u.data)})` : ''}`).join(', ')}</span></>}
                {(w.nr_partii || []).length > 0 && <><span style={etykieta}>partie towaru</span><span>{w.nr_partii.join(', ')}</span></>}
                {w.warunki_dostawy && <><span style={etykieta}>warunki dostawy</span><span>{w.warunki_dostawy}</span></>}
                {(w.kurs_umowny || w.waluta_umowna) && <><span style={etykieta}>kurs umowny</span><span>{w.kurs_umowny} {w.waluta_umowna}</span></>}
                {transport.map((t, i) => (
                  <React.Fragment key={i}>
                    <span style={etykieta}>transport</span>
                    <span data-testid="warunki-transport">{[t.rodzaj_opis || t.opis_inny, t.przewoznik?.nazwa ? `przewoźnik ${t.przewoznik.nazwa}` : null, t.nr_zlecenia ? `zlecenie ${t.nr_zlecenia}` : null, t.opis_ladunku, t.wysylka_z ? `z: ${t.wysylka_z}` : null, t.wysylka_do ? `do: ${t.wysylka_do}` : null].filter(Boolean).join(' · ')}</span>
                  </React.Fragment>
                ))}
                {p3.map((p, i) => (
                  <React.Fragment key={`p3-${i}`}>
                    <span style={etykieta}>{p.rola_opis || 'podmiot trzeci'}</span>
                    <span data-testid="warunki-podmiot3">{p.nazwa || '—'}{p.nip ? ` · NIP ${p.nip}` : ''}{p.adres_tekst ? ` · ${p.adres_tekst}` : ''}</span>
                  </React.Fragment>
                ))}
                {adn.length > 0 && <><span style={etykieta}>adnotacje</span><span data-testid="warunki-adnotacje" style={{ color: 'var(--orange)', fontWeight: 600 }}>{adn.join(' · ')}</span></>}
                {opisy.map((o, i) => (
                  <React.Fragment key={`op-${i}`}>
                    <span style={etykieta}>{o.klucz || 'opis'}</span>
                    <span data-testid="warunki-opis" style={{ whiteSpace: 'pre-line' }}>{o.wartosc || '—'}</span>
                  </React.Fragment>
                ))}
                {sx.korekta && <><span style={etykieta}>korekta</span><span>{[sx.korekta.przyczyna, sx.korekta.typ_opis].filter(Boolean).join(' · ')}</span></>}
                {obc.map((o, i) => (
                  <React.Fragment key={`ob-${i}`}>
                    <span style={etykieta}>{o.typ}</span>
                    <span data-testid="warunki-rozliczenie">{formatujKwote(o.kwota)}{o.powod ? ` — ${o.powod}` : ''}</span>
                  </React.Fragment>
                ))}
                {roz && (roz.do_zaplaty !== null && roz.do_zaplaty !== undefined) && <><span style={etykieta}>do zapłaty (rozliczenie)</span><strong>{formatujKwote(roz.do_zaplaty)}</strong></>}
                {rej.length > 0 && <><span style={etykieta}>rejestry</span><span data-testid="warunki-rejestry">{rej.join(' · ')}</span></>}
              </div>
              {info.length > 0 && (
                <details style={{ marginTop: '6px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '11px', color: 'var(--text-secondary)' }}>Stopka faktury ({info.length})</summary>
                  {info.map((t, i) => <div key={i} data-testid="warunki-stopka" style={{ whiteSpace: 'pre-line', fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>{t}</div>)}
                </details>
              )}
            </div>
          );
        })()}
        {dane?.szczegoly_blad && <div data-testid="ksef-szczegoly-blad" style={{ fontSize: '11px', color: 'var(--danger)', marginBottom: '8px' }}>Nie udało się odczytać szczegółów z XML: {dane.szczegoly_blad}</div>}

        {/* Pozycje */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '6px' }}>
          <span style={{ fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>Pozycje</span>
          {pozycje.length > 0 && !koszty && (
            <span data-testid="ksef-dopasowanie" style={{ fontSize: '11px', color: pokrycie.dopasowane === pokrycie.wszystkie ? 'var(--success)' : 'var(--warning)', fontWeight: 600 }}>
              dopasowano {pokrycie.dopasowane} z {pokrycie.wszystkie ?? pozycje.length} pozycji do produktów
            </span>
          )}
        </div>
        <div style={{ overflow: 'auto', marginBottom: '10px' }}>
          <table data-testid="tabela-pozycji" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'right' }}>Lp</th>
                <th style={th}>Pozycja na fakturze</th>
                {!koszty && <th style={th}>Produkt w systemie</th>}
                <th style={{ ...th, textAlign: 'right' }}>Ilość</th>
                <th style={{ ...th, textAlign: 'right' }}>Cena netto</th>
                <th style={{ ...th, textAlign: 'right' }}>VAT</th>
                <th style={{ ...th, textAlign: 'right' }}>Netto</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto</th>
              </tr>
            </thead>
            <tbody>
              {pozycje.map((p, i) => {
                const brak = !p.produkt;
                return (
                  <tr key={p.lp ?? i} data-testid="pozycja" data-dopasowana={koszty ? undefined : (brak ? 'nie' : 'tak')}
                    style={{ backgroundColor: koszty ? 'transparent' : (brak ? 'var(--warning-light)' : 'var(--success-light)') }}>
                    <td style={{ ...prawa, color: 'var(--text-muted)' }}>{p.lp ?? i + 1}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{p.nazwa_produktu || '—'}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        {p.kod_produktu ? `kod ${p.kod_produktu}` : 'bez kodu'}{p.ean ? ` · EAN ${p.ean}` : ' · bez EAN'}{p.jednostka ? ` · ${p.jednostka}` : ''}
                      </div>
                    </td>
                    {!koszty && <td style={{ ...td, color: brak ? 'var(--warning)' : 'var(--success)' }}>
                      {brak ? <span data-testid="brak-produktu" style={{ fontWeight: 600 }}>brak produktu w systemie</span> : (
                        <>
                          <div style={{ fontWeight: 600 }}>{p.produkt.nazwa || `#${p.produkt.id}`}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                            {p.dopasowanie ? DOPASOWANIA[p.dopasowanie] || p.dopasowanie : 'dopasowano'}{p.produkt.ean ? ` · EAN ${p.produkt.ean}` : ''}{p.produkt.id ? ` · #${p.produkt.id}` : ''}
                          </div>
                        </>
                      )}
                    </td>}
                    <td style={{ ...prawa, fontWeight: 600 }}>{formatujIlosc(p.ilosc)}</td>
                    <td style={prawa}>{formatujKwote(p.cena_netto)}</td>
                    <td style={prawa}>{p.stawka_vat !== null && p.stawka_vat !== undefined ? `${p.stawka_vat}%` : '—'}</td>
                    <td style={prawa}>{formatujKwote(p.wartosc_netto)}</td>
                    <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(p.wartosc_brutto)}</td>
                  </tr>
                );
              })}
              {pozycje.length > 0 && (
                <tr data-testid="pozycje-suma">
                  <td colSpan={koszty ? 5 : 6} style={{ ...td, textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Razem</td>
                  <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(sumaNetto)}</td>
                  <td style={{ ...prawa, fontWeight: 700, color: 'var(--orange)' }}>{formatujKwote(sumaBrutto)}</td>
                </tr>
              )}
            </tbody>
          </table>
          {!ladowanie && pozycje.length === 0 && (
            <div data-testid="brak-pozycji" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              {f.ma_xml ? 'Brak pozycji w XML faktury' : 'Brak XML — pozycje pojawią się po pobraniu pliku z KSeF'}
            </div>
          )}
          {ladowanie && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie…</div>}
        </div>

        {/* Braki produktow (tylko tryb magazynowy - koszty nie ida do produktow) */}
        {brakujace.length > 0 && !koszty && (
          <div data-testid="brakujace" style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--warning-light)', border: '1px solid var(--warning)', color: 'var(--warning)' }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              Brak {brakujace.length} {brakujace.length === 1 ? 'produktu' : 'produktów'} w systemie — faktura nie wejdzie, parser nie zakłada produktów
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '11px' }}>
              {brakujace.map((b, i) => (
                <li key={`${b.kod_sfd || i}-${b.ean || ''}`}>{b.nazwa || '—'} <span style={{ color: 'var(--text-muted)' }}>(kod SFD {b.kod_sfd || '—'}, ilość {formatujIlosc(b.ilosc)}, EAN {b.ean || 'brak'})</span></li>
              ))}
            </ul>
            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text)' }}>Dodaj te produkty z cennika i otwórz podgląd ponownie.</div>
          </div>
        )}

        {/* Stopka */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={onZamknij} style={btnSecondary} data-testid="modal-zamknij">Zamknij</button>
          <AkcjeXml wiersz={f} onXml={zrodlo?.pobierzXml ? (w) => pobierzXmlDoPliku(w, zrodlo.pobierzXml) : undefined} />
          {f.status !== 'dodana' && !koszty && (
            <button type="button" onClick={dopasuj} disabled={dopasowywanie || ladowanie} data-testid="btn-dopasuj"
              title="Ponownie dopasuje fakturę do systemu (numer, NIP, kwota) i do Kontroli faktur SFD; odświeży podgląd pozycji"
              style={{ ...btnSecondary, border: '1px solid var(--primary)', color: 'var(--primary)', opacity: dopasowywanie ? 0.6 : 1 }}>
              {dopasowywanie ? 'Dopasowuję…' : 'Dopasuj ponownie'}
            </button>
          )}
          {!ladowanie && !koszty && (
            <AkcjeDodaj wiersz={f} sklepy={listaSklepow} kompakt={false} widoczny={dodajWidoczny} brakujace={brakujace}
              onDodano={(wynik, w) => { if (onDodano) onDodano(wynik, w); laduj(); }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SzczegolyKsefModal;
